import { useEffect, useState } from 'react';
import { useT } from '../i18n/locale-context';

// TZ 2026-08-28 §1: 100% of people who re-requested a code (237/237) confirmed.
// So the resend is the single strongest lever on the 26% drop — it must read as
// a rescue, not hide behind a timer in muted text. ~60s so a slow-but-arriving
// email lands first (the median 1st→2nd request gap was 9 min), then it turns
// into a prominent, one-tap offer with an explicit «check Spam».
const DEFAULT_COOLDOWN_S = 60;

/**
 * «Не пришло письмо? → Отправить код ещё раз» for the email-OTP wait screens
 * (register + login). While the cooldown ticks it stays a quiet line WITH the
 * spam hint already visible (the #1 reason mail "doesn't arrive"); once it
 * expires it becomes a bordered call-to-action, because a resend is the rescue,
 * not a footnote.
 */
export function OtpResendButton({
  onResend,
  cooldownSeconds = DEFAULT_COOLDOWN_S,
}: {
  onResend: () => Promise<void>;
  cooldownSeconds?: number;
}) {
  const t = useT();
  const [remaining, setRemaining] = useState(cooldownSeconds);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [remaining]);

  // No spam line here: the «check Spam» reassurance lives once already — in the
  // code-sent hint under the field (or the iCloud note), so repeating it under the
  // resend would be the third copy on one screen.
  if (remaining > 0) {
    return (
      <p className="text-center text-[12.5px] text-ink-muted">
        {t('auth.otp.resendIn', { seconds: remaining })}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-edge bg-bone p-3 text-center">
      <p className="text-[13px] font-semibold text-ink">{t('auth.otp.notArrived')}</p>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void onResend()
            .then(() => setRemaining(cooldownSeconds))
            .finally(() => setBusy(false));
        }}
        className="h-9 w-full rounded-md border border-brand bg-bone-surface text-[13.5px] font-bold text-brand transition-colors hover:bg-brand-dim disabled:opacity-60"
      >
        {t('auth.otp.resend')}
      </button>
    </div>
  );
}
